import { useState, useMemo } from 'react'
import { X, Check, Users, Trash2, Target, TrendingDown } from 'lucide-react'
import { useMatch } from '../hooks/useMatches'
import { useUpdateRoundSettings } from '../hooks/useGames'
import { useSideBets, useCreateSideBet, useDeleteSideBet } from '../hooks/useSideBets'
import { GAME_TYPES } from '../constants/gameTypes'
import { buildGameHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { hexToRgba, getInitials } from '../utils/colors'
import './Modal.css'

export default function RoundSettingsEditor({ game, matchId, onClose }) {
  const { data: match } = useMatch(matchId)
  const updateSettings = useUpdateRoundSettings()
  const { data: sideBets } = useSideBets(matchId)
  const createSideBet = useCreateSideBet()
  const deleteSideBet = useDeleteSideBet()

  const matchPlayers = match?.match_players || []
  const gameTeams = game.teams || []
  const gameType = GAME_TYPES.find((g) => g.id === game.game_type) || null

  // Initialize state from existing game data
  const [gameName, setGameName] = useState(game.game_name || '')
  const [wager, setWager] = useState(game.wager != null ? String(game.wager) : '')
  const [teams, setTeams] = useState(() =>
    gameTeams
      .sort((a, b) => a.team_order - b.team_order)
      .map((t) => ({
        name: t.team_name,
        playerIds: (t.team_players || []).map((tp) => tp.player_id),
      }))
  )

  // Side bets state
  const roundCtp = (sideBets || []).find((b) => b.bet_type === 'ctp' && b.game_id === game.id)
  const roundLowNet = (sideBets || []).find((b) => b.bet_type === 'low_net' && b.game_id === game.id)
  const [ctpEnabled, setCtpEnabled] = useState(!!roundCtp)
  const [ctpAmount, setCtpAmount] = useState(roundCtp ? String(roundCtp.amount) : '5')
  const [roundLowNetEnabled, setRoundLowNetEnabled] = useState(!!roundLowNet)
  const [roundLowNetAmount, setRoundLowNetAmount] = useState(roundLowNet ? String(roundLowNet.amount) : '10')

  const roundSkins = (sideBets || []).find((b) => (b.bet_type === 'skins' || b.bet_type === 'skins_carry') && b.game_id === game.id)
  const [roundSkinsEnabled, setRoundSkinsEnabled] = useState(!!roundSkins)
  const [roundSkinsAmount, setRoundSkinsAmount] = useState(roundSkins ? String(roundSkins.amount) : '5')
  const [roundSkinsCarryOver, setRoundSkinsCarryOver] = useState(roundSkins?.bet_type === 'skins_carry')
  const [roundSkinsGrossTiebreaker, setRoundSkinsGrossTiebreaker] = useState(roundSkins?.config?.gross_tiebreaker ?? false)
  const [roundSkinsFullHandicap, setRoundSkinsFullHandicap] = useState(roundSkins?.config?.full_handicap ?? false)

  const course = getGameCourse(game)
  const tee = getGameTee(game)
  const par3Count = course ? course.par.filter((p) => p === 3).length : 0
  const isScramble = game.game_type === 'scramble'
  const lowNetDisabled = game.game_type === 'net_stroke_play' || isScramble
  const skinsDisabled = isScramble
  const isIndividualGame = gameType?.teamSizes?.length === 1 && gameType?.teamSizes[0] === 1

  // Handicap breakdown for display
  const handicapBreakdown = useMemo(() => {
    return buildGameHandicapBreakdown({
      gameType: game.game_type, teamStructure: teams, matchPlayers, course, tee,
      mixedTees: getMixedTees(game),
    }) || []
  }, [course, tee, matchPlayers, game.game_type, teams])

  const getHandicapInfo = (playerId) => handicapBreakdown.find((h) => h.playerId === playerId)

  const getPlayerByPlayerId = (playerId) => {
    const mp = matchPlayers.find((mp) => mp.player_id === playerId)
    return mp?.player
  }

  // Team management
  const assignedPlayerIds = teams.flatMap((t) => t.playerIds)
  const gamePlayers = (game.game_players || []).map((gp) => gp.player_id)
  const unassignedPlayers = gamePlayers.filter((pid) => !assignedPlayerIds.includes(pid))

  const addPlayerToTeam = (teamIndex, playerId) => {
    setTeams((prev) => prev.map((t, i) =>
      i === teamIndex ? { ...t, playerIds: [...t.playerIds, playerId] } : t
    ))
  }

  const removePlayerFromTeam = (teamIndex, playerId) => {
    setTeams((prev) => prev.map((t, i) =>
      i === teamIndex ? { ...t, playerIds: t.playerIds.filter((id) => id !== playerId) } : t
    ))
  }

  const renameTeam = (index, name) => {
    setTeams((prev) => prev.map((t, i) => i === index ? { ...t, name } : t))
  }

  const [addingToTeam, setAddingToTeam] = useState(null)

  const handleSave = async () => {
    // Update game settings
    updateSettings.mutate(
      {
        gameId: game.id,
        game_name: gameName.trim(),
        wager: parseFloat(wager) || 0,
        teams: teams.map((t) => ({ name: t.name, playerIds: t.playerIds })),
      },
      {
        onSuccess: () => {
          // Handle side bet changes
          handleSideBetChanges()
          onClose()
        },
      }
    )
  }

  const handleSideBetChanges = () => {
    // CTP
    if (ctpEnabled && !roundCtp) {
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'ctp', amount: parseFloat(ctpAmount) || 5, scope: 'round' })
    } else if (!ctpEnabled && roundCtp) {
      deleteSideBet.mutate(roundCtp.id)
    } else if (ctpEnabled && roundCtp && Number(ctpAmount) !== Number(roundCtp.amount)) {
      deleteSideBet.mutate(roundCtp.id)
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'ctp', amount: parseFloat(ctpAmount) || 5, scope: 'round' })
    }

    // Low Net
    if (roundLowNetEnabled && !roundLowNet) {
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'low_net', amount: parseFloat(roundLowNetAmount) || 10, scope: 'round' })
    } else if (!roundLowNetEnabled && roundLowNet) {
      deleteSideBet.mutate(roundLowNet.id)
    } else if (roundLowNetEnabled && roundLowNet && Number(roundLowNetAmount) !== Number(roundLowNet.amount)) {
      deleteSideBet.mutate(roundLowNet.id)
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: 'low_net', amount: parseFloat(roundLowNetAmount) || 10, scope: 'round' })
    }

    // Skins
    const newSkinsBetType = roundSkinsCarryOver ? 'skins_carry' : 'skins'
    const newSkinsConfig = (roundSkinsGrossTiebreaker || roundSkinsFullHandicap) ? { ...(roundSkinsGrossTiebreaker && { gross_tiebreaker: true }), ...(roundSkinsFullHandicap && { full_handicap: true }) } : undefined
    const skinsConfigChanged = !!roundSkinsGrossTiebreaker !== !!roundSkins?.config?.gross_tiebreaker || !!roundSkinsFullHandicap !== !!roundSkins?.config?.full_handicap
    if (roundSkinsEnabled && !roundSkins) {
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: newSkinsBetType, amount: parseFloat(roundSkinsAmount) || 5, scope: 'round', config: newSkinsConfig })
    } else if (!roundSkinsEnabled && roundSkins) {
      deleteSideBet.mutate(roundSkins.id)
    } else if (roundSkinsEnabled && roundSkins && (Number(roundSkinsAmount) !== Number(roundSkins.amount) || newSkinsBetType !== roundSkins.bet_type || skinsConfigChanged)) {
      deleteSideBet.mutate(roundSkins.id)
      createSideBet.mutate({ match_id: matchId, game_id: game.id, bet_type: newSkinsBetType, amount: parseFloat(roundSkinsAmount) || 5, scope: 'round', config: newSkinsConfig })
    }
  }

  const canSave = teams.length >= 2 && teams.every((t) => t.playerIds.length > 0) && unassignedPlayers.length === 0

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Round Settings</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-form" style={{ flex: 1, overflowY: 'auto' }}>
          {/* Round Name */}
          <div className="form-group">
            <label>Round Name</label>
            <input
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              placeholder="e.g. Day 1 Morning"
            />
          </div>

          {/* Stakes */}
          <div className="form-group">
            <label>Stakes per Segment</label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                fontWeight: '600',
                pointerEvents: 'none',
              }}>$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={wager}
                onChange={(e) => setWager(e.target.value)}
                placeholder="e.g. 20"
                style={{ paddingLeft: '1.75rem' }}
              />
            </div>
            {gameType && ['nassau', 'stableford', 'six_pack'].includes(gameType.scoring) && wager && Number(wager) > 0 && (() => {
              const weights = game.game_config?.nassauWeights || [1, 1, 1]
              const weightSum = weights.reduce((a, b) => a + b, 0)
              const isWeighted = weights.some((w, i) => w !== 1)
              return (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                  {weightSum} × ${Number(wager)} = ${Number(wager) * weightSum}/pp total{isWeighted ? ' (1-1-2: overall pays double)' : ' (front 9, back 9, overall 18)'}
                </p>
              )
            })()}
          </div>

          {/* Side Games */}
          <div className="form-section" style={{ marginTop: '0.5rem' }}>
            <h3>Side Games</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {/* CTP */}
              <div
                onClick={() => setCtpEnabled(!ctpEnabled)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: ctpEnabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                  borderRadius: '0.5rem',
                  border: `1px solid ${ctpEnabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '0.25rem',
                  border: `2px solid ${ctpEnabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: ctpEnabled ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {ctpEnabled && <Check size={14} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Target size={14} style={{ color: ctpEnabled ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Closest to Pin</span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                    Per player per par 3 hole
                  </div>
                </div>
                {ctpEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                    <input
                      type="number"
                      min="1"
                      value={ctpAmount}
                      onChange={(e) => setCtpAmount(e.target.value)}
                      style={{
                        width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                        fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                      }}
                    />
                  </div>
                )}
                {ctpEnabled && par3Count > 0 && (
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem', width: '100%', paddingLeft: '1.75rem' }}>
                    {par3Count} par 3s × ${Number(ctpAmount) || 0} = <span style={{ color: 'var(--accent-primary)', fontWeight: '600' }}>${par3Count * (Number(ctpAmount) || 0)}/player</span> total CTP
                  </div>
                )}
              </div>

              {/* Low Net */}
              <div
                onClick={() => !lowNetDisabled && setRoundLowNetEnabled(!roundLowNetEnabled)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: roundLowNetEnabled && !lowNetDisabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                  borderRadius: '0.5rem',
                  border: `1px solid ${roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  cursor: lowNetDisabled ? 'not-allowed' : 'pointer',
                  opacity: lowNetDisabled ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '0.25rem',
                  border: `2px solid ${roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {roundLowNetEnabled && !lowNetDisabled && <Check size={14} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <TrendingDown size={14} style={{ color: roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Lowest Net</span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                    {lowNetDisabled ? (isScramble ? 'Not available for Scramble — no individual scores' : 'Not available — Net Stroke Play is effectively the same game') : 'Low net score for this round wins the pot'}
                  </div>
                </div>
                {roundLowNetEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                    <input
                      type="number"
                      min="1"
                      value={roundLowNetAmount}
                      onChange={(e) => setRoundLowNetAmount(e.target.value)}
                      style={{
                        width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                        fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Skins */}
              <div
                onClick={() => !skinsDisabled && setRoundSkinsEnabled(!roundSkinsEnabled)}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: roundSkinsEnabled && !skinsDisabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
                  borderRadius: '0.5rem',
                  border: `1px solid ${roundSkinsEnabled && !skinsDisabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  cursor: skinsDisabled ? 'not-allowed' : 'pointer',
                  opacity: skinsDisabled ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '0.25rem',
                  border: `2px solid ${roundSkinsEnabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: roundSkinsEnabled ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {roundSkinsEnabled && <Check size={14} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <span style={{ color: roundSkinsEnabled ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: '700', fontSize: '14px' }}>$</span>
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Skins</span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                    {skinsDisabled ? 'Not available for Scramble — no individual scores' : 'Low net per hole wins a skin. Pot split by skins won'}
                  </div>
                </div>
                {roundSkinsEnabled && !skinsDisabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                    <input
                      type="number"
                      min="1"
                      value={roundSkinsAmount}
                      onChange={(e) => setRoundSkinsAmount(e.target.value)}
                      style={{
                        width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                        fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                      }}
                    />
                  </div>
                )}
                {roundSkinsEnabled && (
                  <div
                    onClick={(e) => { e.stopPropagation(); setRoundSkinsCarryOver(!roundSkinsCarryOver) }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      paddingTop: '0.5rem',
                      paddingLeft: '2rem',
                      marginTop: '0.25rem',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '0.1875rem',
                      border: `2px solid ${roundSkinsCarryOver ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                      backgroundColor: roundSkinsCarryOver ? 'var(--accent-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {roundSkinsCarryOver && <Check size={10} style={{ color: '#fff' }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Allow Carry-Overs</span>
                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied holes roll skins to the next hole</div>
                    </div>
                  </div>
              )}
              {roundSkinsEnabled && (
                  <div
                    onClick={(e) => { e.stopPropagation(); setRoundSkinsGrossTiebreaker(!roundSkinsGrossTiebreaker) }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      paddingTop: '0.5rem',
                      paddingLeft: '2rem',
                      marginTop: '0.25rem',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '0.1875rem',
                      border: `2px solid ${roundSkinsGrossTiebreaker ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                      backgroundColor: roundSkinsGrossTiebreaker ? 'var(--accent-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {roundSkinsGrossTiebreaker && <Check size={10} style={{ color: '#fff' }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Low Gross Wins</span>
                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied net scores broken by lower gross</div>
                    </div>
                  </div>
              )}
              {roundSkinsEnabled && (
                  <div
                    onClick={(e) => { e.stopPropagation(); setRoundSkinsFullHandicap(!roundSkinsFullHandicap) }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', paddingTop: '0.5rem', paddingLeft: '2rem', marginTop: '0.25rem', cursor: 'pointer' }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '0.1875rem',
                      border: `2px solid ${roundSkinsFullHandicap ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                      backgroundColor: roundSkinsFullHandicap ? 'var(--accent-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {roundSkinsFullHandicap && <Check size={10} style={{ color: '#fff' }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Full Course Handicap</span>
                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Use 100% course handicap instead of game handicaps</div>
                    </div>
                  </div>
              )}
            </div>
          </div>
          </div>

          {/* Teams / Players */}
          <div className="form-section" style={{ marginTop: '1rem' }}>
            <h3>{isIndividualGame ? 'Players' : 'Teams'}</h3>

            {unassignedPlayers.length > 0 && (
              <div style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '0.5rem',
                marginBottom: '0.75rem',
                border: '1px dashed var(--border-color)',
                textAlign: 'center',
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
                  {unassignedPlayers.length} player{unassignedPlayers.length !== 1 ? 's' : ''} unassigned
                </span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {teams.map((team, teamIdx) => (
                <div
                  key={teamIdx}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={team.name}
                      onChange={(e) => renameTeam(teamIdx, e.target.value)}
                      style={{
                        fontWeight: '700',
                        fontSize: '0.875rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'var(--text-primary)',
                        padding: '0.25rem 0',
                        width: '120px',
                      }}
                    />
                  </div>

                  {/* Players on this team */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
                    {team.playerIds.map((pid) => {
                      const player = getPlayerByPlayerId(pid)
                      if (!player) return null
                      const hcp = getHandicapInfo(pid)
                      return (
                        <div
                          key={pid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.375rem 0.5rem',
                            backgroundColor: 'var(--bg-tertiary)',
                            borderRadius: '0.375rem',
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: player.avatar_color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '0.625rem',
                            color: '#fff',
                            flexShrink: 0,
                          }}>
                            {getInitials(player)}
                          </div>
                          <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                            {player.display_name}
                            {hcp && (
                              <span style={{ color: 'var(--text-primary)', fontWeight: '400' }}> ({hcp.courseHandicap})</span>
                            )}
                          </span>
                          {hcp && (
                            <span style={{ fontSize: '0.8125rem', color: '#ef4444', fontWeight: '600' }}>
                              {hcp.adjustedStrokes} {hcp.adjustedStrokes === 1 ? 'stroke' : 'strokes'}
                            </span>
                          )}
                          {addingToTeam === teamIdx && (
                            <button
                              onClick={() => removePlayerFromTeam(teamIdx, pid)}
                              style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )
                    })}
                    {team.playerIds.length === 0 && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem 0' }}>
                        No players
                      </p>
                    )}
                  </div>

                  {/* Add/Edit Players */}
                  {addingToTeam !== teamIdx && (unassignedPlayers.length > 0 || team.playerIds.length > 0) && (
                    <button
                      onClick={() => setAddingToTeam(teamIdx)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.375rem',
                        padding: '0.625rem',
                        borderRadius: '9999px',
                        backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                        border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
                        color: 'var(--accent-primary)',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                      }}
                    >
                      <Users size={16} />
                      Move Players
                    </button>
                  )}

                  {addingToTeam === teamIdx && (
                    <div style={{
                      borderRadius: '0.5rem',
                      border: '1px solid var(--accent-primary)',
                      overflow: 'hidden',
                    }}>
                      {unassignedPlayers.length > 0 && (
                        <>
                          <div style={{
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                            borderBottom: '1px solid var(--border-color)',
                          }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                              Select players to add
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {unassignedPlayers.map((pid) => {
                              const player = getPlayerByPlayerId(pid)
                              if (!player) return null
                              return (
                                <button
                                  key={pid}
                                  onClick={() => addPlayerToTeam(teamIdx, pid)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.625rem',
                                    padding: '0.75rem',
                                    backgroundColor: 'var(--bg-tertiary)',
                                    borderBottom: '1px solid var(--border-color)',
                                    textAlign: 'left',
                                  }}
                                >
                                  <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: player.avatar_color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '700',
                                    fontSize: '0.6875rem',
                                    color: '#fff',
                                    flexShrink: 0,
                                  }}>
                                    {getInitials(player)}
                                  </div>
                                  <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                    {player.display_name}
                                  </span>
                                  <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                                    Add
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        </>
                      )}
                      <button
                        onClick={() => setAddingToTeam(null)}
                        style={{
                          width: '100%',
                          padding: '0.625rem',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                          color: 'var(--accent-primary)',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                        }}
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <button type="button" className="btn-secondary btn-action" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-action"
            onClick={handleSave}
            disabled={!canSave || updateSettings.isPending}
            style={{ opacity: (!canSave || updateSettings.isPending) ? 0.5 : 1 }}
          >
            {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
